/**
 * <mega-menu> Web Component
 * Handles hover intent, click fallback, keyboard navigation,
 * focus trap, and ARIA state management for mega menu panels.
 */
class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.panel = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.HOVER_DELAY = 300;
    this.isOpen = false;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.mega-menu__trigger');
    this.panel = this.querySelector('.mega-menu__content');

    if (!this.trigger || !this.panel) return;

    /* Store bound references for clean removal */
    this._onMouseEnter = this.handleMouseEnter.bind(this);
    this._onMouseLeave = this.handleMouseLeave.bind(this);
    this._onClick = this.handleClick.bind(this);
    this._onTriggerKey = this.handleTriggerKeydown.bind(this);
    this._onPanelKey = this.handlePanelKeydown.bind(this);
    this._onOutsideClick = this.handleOutsideClick.bind(this);
    this._onResize = () => {
      if (!window.matchMedia('(min-width: 990px)').matches) {
        const restoreFocus = this.contains(document.activeElement) ||
          (this.isOpen && document.activeElement === document.body);
        this.clearTimers();
        this.close();
        if (restoreFocus) document.querySelector('header-drawer > details > summary')?.focus();
      } else if (this.isOpen) this.positionPanel();
    };
    window.addEventListener('resize', this._onResize);

    /* Hover intent — desktop only */
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);

    /* Click fallback */
    this.trigger.addEventListener('click', this._onClick);

    /* Keyboard */
    this.trigger.addEventListener('keydown', this._onTriggerKey);
    this.panel.addEventListener('keydown', this._onPanelKey);

    /* Close on outside click */
    document.addEventListener('click', this._onOutsideClick);

    /* Shopify theme editor: auto-open panel when block is selected */
    if (Shopify.designMode) {
      this._onBlockSelect = this.handleEditorBlockSelect.bind(this);
      this._onBlockDeselect = this.handleEditorBlockDeselect.bind(this);
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);
    if (this.trigger) {
      this.trigger.removeEventListener('click', this._onClick);
      this.trigger.removeEventListener('keydown', this._onTriggerKey);
    }
    if (this.panel) {
      this.panel.removeEventListener('keydown', this._onPanelKey);
    }
    document.removeEventListener('click', this._onOutsideClick);
    window.removeEventListener('resize', this._onResize);
    if (this._onBlockSelect) {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
    }
    this.clearTimers();
  }

  /* --- Hover intent --- */
  handleMouseEnter() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.clearTimers();
    this.openTimer = setTimeout(() => this.open(), this.HOVER_DELAY);
  }

  handleMouseLeave() {
    if (this.editorLocked) return;
    this.clearTimers();
    this.closeTimer = setTimeout(() => this.close(), this.HOVER_DELAY);
  }

  /* --- Click --- */
  handleClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOpen) {
      this.close();
    } else {
      this.closeAllOthers();
      this.open();
    }
  }

  /* --- Keyboard on trigger --- */
  handleTriggerKeydown(event) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen) {
          this.close();
        } else {
          this.closeAllOthers();
          this.open();
          this.focusFirstItem();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.closeAllOthers();
          this.open();
        }
        this.focusFirstItem();
        break;

      case 'Escape':
        if (this.isOpen) {
          event.preventDefault();
          this.close();
          this.trigger.focus();
        }
        break;
    }
  }

  /* --- Keyboard inside panel --- */
  handlePanelKeydown(event) {
    const focusable = this.getFocusableElements();
    const currentIndex = focusable.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        this.trigger.focus();
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (currentIndex < focusable.length - 1) {
          focusable[currentIndex + 1].focus();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (currentIndex > 0) {
          focusable[currentIndex - 1].focus();
        } else {
          this.trigger.focus();
        }
        break;

      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        /* Move focus to the first focusable element in the adjacent column */
        const columns = Array.from(
          this.panel.querySelectorAll('.mega-menu__link-column, .mega-menu__product-column')
        );
        const currentCol = document.activeElement.closest(
          '.mega-menu__link-column, .mega-menu__product-column'
        );
        const colIndex = columns.indexOf(currentCol);
        if (colIndex === -1) break;
        const nextColIndex = event.key === 'ArrowRight'
          ? Math.min(colIndex + 1, columns.length - 1)
          : Math.max(colIndex - 1, 0);
        if (nextColIndex === colIndex) break;
        const nextColFocusable = columns[nextColIndex].matches('a[href]') ? columns[nextColIndex] : columns[nextColIndex].querySelector(
          'a[href], button:not([disabled])'
        );
        if (nextColFocusable) nextColFocusable.focus();
        break;
      }

      case 'Tab':
        /* Tab from first/last item exits panel (not a focus trap — per spec 6.3) */
        if (event.shiftKey && currentIndex === 0) {
          event.preventDefault();
          this.close();
          this.trigger.focus();
        } else if (!event.shiftKey && currentIndex === focusable.length - 1) {
          event.preventDefault();
          this.close();
          /* Focus next sibling menu trigger */
          const nextTrigger = this.getNextMenuTrigger();
          (nextTrigger || this.trigger).focus();
        }
        break;
    }
  }

  /* --- Outside click --- */
  handleOutsideClick(event) {
    if (this.editorLocked) return;
    if (this.isOpen && !this.contains(event.target)) {
      this.close();
    }
  }

  /* --- Open / Close --- */
  open() {
    if (this.isOpen || !window.matchMedia('(min-width: 990px)').matches) return;
    this.closeAllOthers();
    this.isOpen = true;
    this.classList.add('mega-menu--open');
    this.trigger.setAttribute('aria-expanded', 'true');

    /* Notify hero-journey (transparent header) */
    document.dispatchEvent(new CustomEvent('mega-menu:open'));

    /* Transition header to panel's brand world */
    const headerWrapper = this.closest('.header-wrapper');
    this.previousHeaderBrand = headerWrapper?.dataset.brand || '';
    const panelBrand = this.panel.dataset.brand;
    if (panelBrand && headerWrapper) {
      headerWrapper.dataset.headerBrand = panelBrand;
      headerWrapper.dataset.brand = panelBrand;
    }
    this.positionPanel();
  }

  positionPanel() {
    if (!this.panel || this.panel.classList.contains('mega-menu__content--full-width')) return;
    const parent = this.panel.offsetParent;
    if (!parent) return;
    const origin = parent.getBoundingClientRect().left;
    const gutter = parseFloat(getComputedStyle(this).getPropertyValue('--layout-gutter')) || 24;
    const desired = this.trigger.getBoundingClientRect().left;
    const maximum = document.documentElement.clientWidth - gutter - this.panel.getBoundingClientRect().width;
    this.panel.style.left = (Math.max(gutter, Math.min(desired, maximum)) - origin) + 'px';
    this.panel.style.right = 'auto';
  }

  close() {
    if (!this.isOpen) return;
    this.clearTimers();
    this.isOpen = false;
    this.classList.remove('mega-menu--open');
    this.trigger.setAttribute('aria-expanded', 'false');


    /* Reset content-hugging position */
    this.panel.style.left = '';
    this.panel.style.right = '';

    /* Revert header to page default brand */
    const headerWrapper = this.closest('.header-wrapper');
    if (headerWrapper) {
      delete headerWrapper.dataset.headerBrand;
      if (typeof this.previousHeaderBrand === 'string' && this.previousHeaderBrand) {
        headerWrapper.dataset.brand = this.previousHeaderBrand;
      } else if (typeof this.previousHeaderBrand === 'string') {
        delete headerWrapper.dataset.brand;
      }
    }
    this.previousHeaderBrand = undefined;
    /* Restore hero transparency after the page brand tokens have been restored. */
    document.dispatchEvent(new CustomEvent('mega-menu:close'));
  }

  closeAllOthers() {
    document.querySelectorAll('mega-menu.mega-menu--open').forEach((menu) => {
      if (menu !== this) menu.close();
    });
  }

  /* --- Focus helpers --- */
  getFocusableElements() {
    return Array.from(
      this.panel.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  focusFirstItem() {
    const items = this.getFocusableElements();
    if (items.length > 0) items[0].focus();
  }

  getNextMenuTrigger() {
    const allTriggers = Array.from(document.querySelectorAll(
      'a[href], button:not([disabled]), summary, input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex="0"]'
    )).filter((element) => !element.closest('.mega-menu__content, [hidden], [inert]') &&
      element.getClientRects().length && getComputedStyle(element).visibility === 'visible');
    const currentIndex = allTriggers.indexOf(this.trigger);
    return currentIndex < allTriggers.length - 1
      ? allTriggers[currentIndex + 1]
      : null;
  }

  /* --- Shopify editor integration --- */
  handleEditorBlockSelect(event) {
    const selectedEl = event.target;
    /* Open this mega menu if the selected block is inside it (or is it) */
    if (this.contains(selectedEl) || this === selectedEl) {
      this.closeAllOthers();
      this.open();
      this.editorLocked = true;
    }
  }

  handleEditorBlockDeselect(event) {
    const deselectedEl = event.target;
    if (this.contains(deselectedEl) || this === deselectedEl) {
      this.editorLocked = false;
      this.close();
    }
  }

  /* --- Timers --- */
  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openTimer = null;
    this.closeTimer = null;
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}


